import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { getOtherRefSearchDropdown } from "../../api/keyvalue";
import LoadingSpinner from "../LoadingSpinner";
import Dropdown from "../input/Dropdown";
import Input from "../input/Input";
import SearchDropdown from "../input/SearchDropdown";
import { clusterValidation } from "../validations/ClusterCreation";
import InfoList from "../InfoList";
import { formatFromJsonDate } from "../../dateConfig";
import { User } from "react-feather";
import { FORMS, MODULES } from "../../const";
import useUserCompany from "../../hooks/useUserCompany";

const FORM = FORMS.CLUSTER.CLU_DETAIL.form;

const ClusterComponent = forwardRef(
  ({ errors, dropdown, isLoading, currentValues, editMode }, ref) => {
    const userCompany = useUserCompany();

    const [company, setCompany] = useState(
      userCompany ? userCompany.comId : null,
    );

    const [clusterData, setClusterData] = useState(
      editMode
        ? {
            ...currentValues,
            clnBilling: currentValues.clnBillingId,
            clnInvoice: currentValues.clnInvoiceId,
          }
        : {
            clnCompany: company,
            clnCode: "",
            clnCluster: "",
            clnDescription: "",
            clnBilling: "",
            clnInvoice: "",
            clnActive: 1,
            clnStatus: 2,
          },
    );
    const [initialActive, setInitialActive] = useState(
      editMode ? currentValues?.clnActive : 1,
    );

    const [currentErrors, setCurrentErrors] = useState({});
    useEffect(() => {
      setCurrentErrors(errors);
    }, [errors]);

    useImperativeHandle(ref, () => ({
      getData: () => clusterData,
      validateForm: () => {
        return clusterValidation(clusterData);
      },
      isDeletedActive: () => clusterData.clnActive === 2 && initialActive !== 2,
      resetForm: () => {
        setClusterData({
          clnCode: "",
          clnCluster: "",
          clnDescription: "",
          clnBilling: "",
          clnInvoice: "",
          clnActive: 1,
          clnStatus: 2,
        });
      },
    }));

    const [clusterCode, setClusterCode] = useState("");
    const [clusterName, setClusterName] = useState("");
    const [description, setDescription] = useState("");
    const [accountingStakeholder, setAccountingStakeholder] = useState("");
    const [billingStakeholder, setBillingStakeholder] = useState("");
    const [active, setActive] = useState("");
    const [type, setType] = useState("");
    const [value, setValue] = useState("");

    const [searchFieldReset, setSearchFieldReset] = useState(false);

    useEffect(() => {
      setCompany(userCompany ? userCompany.comId : null);
    }, [userCompany]);

    const handleReset = () => {
      setClusterCode("");
      setClusterName("");
      setDescription("");
      setAccountingStakeholder("");
      setBillingStakeholder("");
      setActive("");
      setType("");
      setValue("");
    };

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Cluster</h4>
              <div className="two fields">
                <Input
                  label="Cluster code"
                  error={currentErrors.clnCode}
                  module={MODULES.Master}
                  form={FORM}
                  field="cln_code"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { clnCode: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="clusterCode"
                  value={clusterData.clnCode}
                  onChange={(e) => {
                    setClusterData((prevState) => ({
                      ...prevState,
                      clnCode: e.target.value,
                    }));
                  }}
                  disabled={true}
                />
              </div>
              <div className="two fields">
                <Input
                  required={true}
                  label="Cluster Name"
                  placeholder="Cluster Name"
                  error={currentErrors.clnCluster}
                  module={MODULES.Master}
                  form={FORM}
                  field="cln_cluster"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { clnCluster: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="clusterName"
                  value={clusterData.clnCluster}
                  onChange={(e) => {
                    setClusterData((prevState) => ({
                      ...prevState,
                      clnCluster: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields">
                <div
                  className={`field ${
                    currentErrors.clnDescription ? "error" : ""
                  }`}
                >
                  <Input
                    required={true}
                    name="clusterDescription"
                    label="Description"
                    placeholder="Description"
                    error={currentErrors.clnDescription}
                    module={MODULES.Master}
                    form={FORM}
                    field="cln_description"
                    editMode={editMode}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { clnDescription: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    value={clusterData.clnDescription}
                    onChange={(e) => {
                      setClusterData((prevState) => ({
                        ...prevState,
                        clnDescription: e.target.value,
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="two fields">
                <SearchDropdown
                  key={1}
                  placeholder={"Billing Customer"}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="cln_billing"
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setClusterData((prevState) => ({
                      ...prevState,
                      clnBilling: null,
                    }));
                  }}
                  onResultSelect={(data) =>
                    setClusterData((prevState) => ({
                      ...prevState,
                      clnBilling: data,
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "stakeholder_type",
                    value: "Billing Customer",
                  }}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_stakeholder",
                    primaryKey: "stk_id",
                    fieldName: "stk_name",
                    id: clusterData.clnBilling,
                  }}
                  label="Billing Customer"
                  error={currentErrors.clnBilling}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { clnBilling: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />

                <SearchDropdown
                  key={1}
                  placeholder={"Invoicing Customer"}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="cln_invoice"
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setClusterData((prevState) => ({
                      ...prevState,
                      clnInvoice: null,
                    }));
                  }}
                  onResultSelect={(data) =>
                    setClusterData((prevState) => ({
                      ...prevState,
                      clnInvoice: data,
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "stakeholder_type",
                    value: "Invoicee Customer",
                  }}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_stakeholder",
                    primaryKey: "stk_id",
                    fieldName: "stk_name",
                    id: clusterData.clnInvoice,
                  }}
                  label="Invoicing Customer"
                  error={currentErrors.clnInvoice}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { clnInvoice: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
              </div>
              <div className="two fields">
                <Dropdown
                  label="Status"
                  error={currentErrors.clnStatus}
                  module={MODULES.Master}
                  form={FORM}
                  field="cln_status"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { clnStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Status"
                  options={dropdown.clusterStatusOpt}
                  value={clusterData.clnStatus}
                  style={{ height: "20px" }}
                  onChange={(e, data) => {
                    setClusterData((prevState) => ({
                      ...prevState,
                      clnStatus: data.value,
                    }));
                  }}
                />
                <Dropdown
                  label="Active"
                  error={currentErrors.clnActive}
                  module={MODULES.Master}
                  form={FORM}
                  field="cln_active"
                  authenticated={true}
                  needDeletePermission={true}
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { clnActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Active"
                  options={dropdown.clusterActiveOpt}
                  value={clusterData.clnActive}
                  style={{ height: "20px" }}
                  onChange={(e, data) => {
                    setClusterData((prevState) => ({
                      ...prevState,
                      clnActive: data.value,
                    }));
                  }}
                />
              </div>
            </div>
            {editMode && (
              <InfoList
                title="User Information"
                content={[
                  {
                    title: "Created By",
                    value: clusterData.createdBy
                      ? clusterData.createdBy
                      : "N/A",
                  },
                  {
                    title: "Created Date",
                    value: clusterData.createdDate
                      ? formatFromJsonDate(clusterData.createdDate)
                      : "N/A",
                  },
                  {
                    title: "Last Modified By",
                    value: clusterData.lastModifiedBy
                      ? clusterData.lastModifiedBy
                      : "N/A",
                  },
                  {
                    title: "Last Modified Date",
                    value: clusterData.lastModifiedDate
                      ? formatFromJsonDate(clusterData.lastModifiedDate)
                      : "N/A",
                  },
                ]}
                Icon={User}
              />
            )}
          </form>
        )}
      </>
    );
  },
);

export default ClusterComponent;
